"use client";

/**
 * "Back to top" for the long public pages (FAQ is ~40 answers, Tutorials ~10 guides expanded).
 *
 * A CLIENT LEAF THAT RENDERS ONLY A BUTTON — the same rule <FaqSearch> follows and the reason
 * these pages exist in this shape. The FAQ and Tutorials bodies are SERVER components on
 * purpose: most answer-engine crawlers do not execute JavaScript, so anything that pulls the
 * content into a client component turns the page into an empty shell for them. This owns no
 * content, wraps nothing, and reads nothing but `window.scrollY`.
 *
 * IT DOES NOT EXIST UNTIL THERE IS SOMEWHERE TO GO BACK TO. Hidden until the reader is a
 * screenful and a half down, so it never covers content on a short viewport or appears before
 * it can mean anything.
 *
 * ACCESSIBILITY, all three of which are easy to miss on a floating button:
 *   · it is a real <button> with an accessible name, not a styled <div>;
 *   · `aria-hidden` + `tabIndex={-1}` while invisible, so a keyboard user does not tab into a
 *     control they cannot see;
 *   · the scroll respects `prefers-reduced-motion` — a smooth full-page scroll is exactly the
 *     kind of motion that triggers vestibular symptoms, and the platform gives us the answer.
 */

import { useCallback, useEffect, useState } from "react";

/** Show after roughly one and a half viewports — far enough that "back to top" is a real trip. */
const SHOW_AFTER_VH = 1.5;

export function BackToTop({ label }: { label: string }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setShown(window.scrollY > window.innerHeight * SHOW_AFTER_VH);
    };
    onScroll(); // a reload can restore a deep scroll position; do not wait for a move
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toTop = useCallback(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
    // Return focus to the document start so a keyboard reader lands where the page now is,
    // rather than being left at the bottom with the viewport at the top.
    document.getElementById("main")?.focus?.();
  }, []);

  return (
    <button
      type="button"
      onClick={toTop}
      aria-label={label}
      aria-hidden={!shown}
      tabIndex={shown ? 0 : -1}
      className={`fixed bottom-5 right-5 z-40 flex h-11 w-11 items-center justify-center
        rounded-full border border-border/70 bg-panel/90 text-muted shadow-lg backdrop-blur-sm
        transition-all duration-200 hover:border-yellow hover:text-yellow
        focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-yellow
        ${shown ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0"}`}
    >
      {/* An arrow drawn in SVG rather than a glyph: "↑" renders at wildly different weights
          across fonts and disappears entirely where the fallback lacks it. */}
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 19V5" />
        <path d="m5 12 7-7 7 7" />
      </svg>
    </button>
  );
}
